import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import Swal from "sweetalert2";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { getEmployeesWithActiveContract } from "../services/api_calendar";

// helper: ordering -> sortConfig
const orderingToSortConfig = (ordering) => {
  if (!ordering) {
    return {
      key: null,
      direction: null,
    };
  }

  const direction = ordering.startsWith("-")
    ? "descending"
    : "ascending";

  const key = ordering.replace(/^-/, "");

  return { key, direction };
};

// helper: read ctx from URL or sessionStorage
const readNavCtx = () => {
  const qs = new URLSearchParams(window.location.search);

  const ctxFromUrl = {
    page: qs.get("page"),
    page_size: qs.get("page_size"),
    filter_by: qs.get("filter_by"),
    ordering: qs.get("ordering"),
    cs: qs.get("cs"),
    ce: qs.get("ce"),
  };

  const hasSomething = Object.values(ctxFromUrl).some((v) => v !== null && v !== "");
  if (hasSomething) return ctxFromUrl;

  try {
    const stored = sessionStorage.getItem("employees_nav_ctx");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// helper: compute has next page (prefer backend signals)
const computeHasNextPage = (res) => Boolean(res?.next);

export default function EmployeesPrevNextNav({
  token,
  currentCf,              // codice fiscale attuale
  buildPath,              // (cf, ctx) => "/dipendenti/.."
  tabValue,               // numero tab (solo edit), opzionale
  askUnsaved,             // boolean (solo edit)
  onDiscardUnsaved,       // () => void (solo edit)
  className = "",
}) {
  const [, setLocation] = useLocation();

  const [navCtx, setNavCtx] = useState(null);
  const [navLoading, setNavLoading] = useState(false);
  const [pageEmployees, setPageEmployees] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);

  // 1) carica ctx e pagina attuale
  useEffect(() => {
    const ctx = readNavCtx();
    setNavCtx(ctx);

    if (ctx?.single) {
      setPageEmployees([]);
      setHasNextPage(false);
      return;
    }

    if (!ctx || !token) return;

    const page = Number(ctx.page || 1);
    const pageSize = Number(ctx.page_size || 25);
    const filterBy = ctx.filter_by || "all";
    const sortConfig = orderingToSortConfig(ctx.ordering);

    setNavLoading(true);

    getEmployeesWithActiveContract(
      token,
      null,
      filterBy,
      null,
      page,
      pageSize,
      null,
      null,
      ctx.cs || "",
      ctx.ce || null,
      sortConfig
    )
      .then((res) => {
        const arr = Array.isArray(res?.results) ? res.results : [];
        setPageEmployees(arr);
        setHasNextPage(computeHasNextPage(res, ctx));
      })
      .catch((e) => {
        console.error("nav list error", e);
        setPageEmployees([]);
        setHasNextPage(false);
      })
      .finally(() => setNavLoading(false));
  }, [token, currentCf]);

  const currentIdx = useMemo(
    () => pageEmployees.findIndex((e) => e?.cf === currentCf),
    [pageEmployees, currentCf]
  );

  const canPrev = useMemo(() => {
    if (!navCtx) return false;
    if (navCtx.single) return false;
    if (currentIdx > 0) return true;
    return Number(navCtx.page || 1) > 1;
  }, [navCtx, currentIdx]);

  const canNext = useMemo(() => {
    if (!navCtx) return false;
    if (navCtx.single) return false;
    if (currentIdx >= 0 && currentIdx < pageEmployees.length - 1) return true;
    return hasNextPage;
  }, [navCtx, currentIdx, pageEmployees.length, hasNextPage]);

  const pushWithCtx = (newCf, ctx) => {
    if (!ctx) {
      setLocation(buildPath(newCf, null));
      return;
    }

    const qs = new URLSearchParams({
      page: String(ctx.page || 1),
      page_size: String(ctx.page_size || 25),
      filter_by: ctx.filter_by || "all",
      cs: ctx.cs || "",
      ce: ctx.ce || "",
      ...(ctx.ordering ? { ordering: ctx.ordering } : {}),
      ...(tabValue != null ? { tab: String(tabValue) } : {}),
    });

    const updatedCtx = {
      ...ctx,
      page: String(ctx.page || 1),
      page_size: String(ctx.page_size || 25),
      ordering: ctx.ordering || "",
      ts: Date.now(),
    };

    sessionStorage.setItem(
      "employees_nav_ctx",
      JSON.stringify(updatedCtx)
    );

    setLocation(
      buildPath(newCf, Object.fromEntries(qs.entries()))
    );
  };

  const confirmDiscardIfNeeded = async () => {
    if (!askUnsaved) return true;

    const r = await Swal.fire({
      title: "Modifiche non salvate",
      text: "Hai modifiche non salvate. Cambiando dipendente verranno perse. Vuoi procedere comunque?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "SI, continua",
      cancelButtonText: "NO, rimani",
      allowOutsideClick: false,
    });

    if (!r.isConfirmed) return false;
    onDiscardUnsaved?.();
    return true;
  };

  const goPrev = async () => {
    if (!navCtx || !token) return;
    if (navLoading || !canPrev) return;

    const ok = await confirmDiscardIfNeeded();
    if (!ok) return;

    if (currentIdx > 0) {
      pushWithCtx(pageEmployees[currentIdx - 1].cf, navCtx);
      return;
    }

    const currentPage = Number(navCtx.page || 1);
    if (currentPage <= 1) return;

    const newCtx = { ...navCtx, page: String(currentPage - 1) };
    const sortConfig = orderingToSortConfig(newCtx.ordering);

    setNavLoading(true);
    try {
      const res = await getEmployeesWithActiveContract(
        token,
        null,
        newCtx.filter_by || "all",
        null,
        Number(newCtx.page),
        Number(newCtx.page_size || 25),
        null,
        null,
        newCtx.cs || "",
        newCtx.ce || null,
        sortConfig
      );

      const arr = Array.isArray(res?.results) ? res.results : [];
      if (arr.length) {
        setNavCtx(newCtx);
        setPageEmployees(arr);
        setHasNextPage(computeHasNextPage(res, newCtx));
        pushWithCtx(arr[arr.length - 1].cf, newCtx);
      }
    } finally {
      setNavLoading(false);
    }
  };

  const goNext = async () => {
    if (!navCtx || !token) return;
    if (navLoading || !canNext) return;

    const ok = await confirmDiscardIfNeeded();
    if (!ok) return;

    if (currentIdx >= 0 && currentIdx < pageEmployees.length - 1) {
      pushWithCtx(pageEmployees[currentIdx + 1].cf, navCtx);
      return;
    }

    // fine pagina: prova pagina successiva SOLO se hasNextPage è true
    if (!hasNextPage) return;

    const currentPage = Number(navCtx.page || 1);
    const newCtx = { ...navCtx, page: String(currentPage + 1) };
    const sortConfig = orderingToSortConfig(newCtx.ordering);

    setNavLoading(true);
    try {
      const res = await getEmployeesWithActiveContract(
        token,
        null,
        newCtx.filter_by || "all",
        null,
        Number(newCtx.page),
        Number(newCtx.page_size || 25),
        null,
        null,
        newCtx.cs || "",
        newCtx.ce || null,
        sortConfig
      );

      const arr = Array.isArray(res?.results) ? res.results : [];
      if (arr.length) {
        setNavCtx(newCtx);
        setPageEmployees(arr);
        setHasNextPage(computeHasNextPage(res, newCtx));
        pushWithCtx(arr[0].cf, newCtx);
      } else {
        setHasNextPage(false);
      }
    } finally {
      setNavLoading(false);
    }
  };

  // se non c'è contesto (sei arrivato da link diretto), non mostrare
  if (!navCtx || navCtx.single) return null;

  return (
    <div className={`flex gap-2 ${className}`}>
      <button
        type="button"
        onClick={goPrev}
        disabled={navLoading || !canPrev}
        className="px-4 py-2 text-sm font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition flex items-center gap-2 focus:outline-none rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Precedente
      </button>

      <button
        type="button"
        onClick={goNext}
        disabled={navLoading || !canNext}
        className="px-4 py-2 text-sm font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition flex items-center gap-2 focus:outline-none rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Successivo
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
}